import { Button } from '@edsn/pie-components';
import IconDelete from '@iconify/icons-material-symbols/delete-outline';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AddCprContactModal } from '#pie/components/add-cpr-contact-modal/AddCprContactModal';
import { Icon } from '#pie/components/icon/Icon';
import { Dialog } from '#pie/components/layout/dialog/Dialog';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useToast } from '#pie/components/toast/ToastContext';
import type { ManagementContactGroupMember } from '#pie/internal/api/pie-bff';
import {
  getCprManagementContacts,
  useDeleteContactGroupMembership,
  useGetCprManagementContacts,
} from '#pie/internal/api/pie-bff';

interface RemoveCprContactButtonProps {
  data: ManagementContactGroupMember;
}

export const RemoveCprContactButton = ({
  data,
}: RemoveCprContactButtonProps) => {
  const { addToast } = useToast();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);

  const contactGroupId = data.contactGroup.id;

  const { data: contactGroups } = useGetCprManagementContacts({
    pagination: { pageIndex: 0, pageSize: 1 },
    filter: { contactGroupIds: [contactGroupId] },
  });

  const { data: contactData } = useGetCprManagementContacts({
    filter: {},
    pagination: { pageIndex: 0, pageSize: 1000 },
  });

  const linkedGroups =
    contactData?.items.filter((item) => item.contactId === data.contactId)
      .length ?? 0;

  const requiredGroup =
    contactGroups?.items[0]?.contactGroup.isRequired ?? false;
  const lastInGroup = (contactGroups?.totalCount ?? 0) === 1;

  const lastLinkedGroup = linkedGroups === 1;

  const isBlocked = requiredGroup && lastInGroup;
  const deleteContact = !isBlocked && lastLinkedGroup;

  const { mutate, isPending: isLoading } = useDeleteContactGroupMembership({
    mutation: {
      onError: (error) => {
        addToast({
          message: `${t('common.error.message')}`,
          title: `${t('cpr_management_group_detail.modal.error.toast.title')}`,
          type: 'error',
          error: error,
        });
      },
      onSuccess: () => {
        addToast({
          message: deleteContact
            ? `${t('cpr_management_group_detail.modal.toast.message.delete', { user: data.name })}`
            : `${t('cpr_management_group_detail.modal.toast.message', { user: data.name })}`,
          title: deleteContact
            ? `${t('cpr_management_group_detail.modal.toast.title.delete')}`
            : `${t('cpr_management_group_detail.modal.toast.title')}`,
          type: 'success',
        });
        setIsOpen(false);
        queryClient.invalidateQueries({
          predicate: (m) => {
            return m.queryKey.some(
              (n) =>
                typeof n === 'string' &&
                n.includes(getCprManagementContacts.name),
            );
          },
        });
        queryClient.invalidateQueries({
          queryKey: ['/cpr-management-contact'],
        });
      },
    },
  });

  const messageKey = isBlocked
    ? 'cpr_management_group_detail.modal.message.is_blocked'
    : deleteContact
      ? 'cpr_management_group_detail.modal.message.delete'
      : 'cpr_management_group_detail.modal.message.unlink';

  return (
    <>
      <Button
        iconStart={<Icon icon={IconDelete} />}
        action="tertiary"
        emphasis="minimal"
        className="ml-auto p-0"
        aria-label={t('cpr_management_group_detail.modal.title.unlink')}
        onClick={() => setIsOpen(true)}
      />
      <Dialog
        title={
          isBlocked
            ? t('cpr_management_group_detail.modal.title.is_blocked')
            : deleteContact
              ? t('cpr_management_group_detail.modal.title.delete')
              : t('cpr_management_group_detail.modal.title.unlink')
        }
        open={isOpen}
        onOpenChange={setIsOpen}
      >
        <Stack gap="lg">
          <Text>
            <Trans
              i18nKey={messageKey}
              values={{ email: data.email, user: data.name }}
            />
          </Text>
          <div className="grid-auto grid auto-cols-fr grid-flow-col gap-2">
            <Button
              action="tertiary"
              emphasis="subtle"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              {isBlocked ? t('common.close') : t('common.button.cancel')}
            </Button>
            {isBlocked ? (
              <Button
                onClick={() => {
                  setIsOpen(false);
                  setIsAddContactOpen(true);
                }}
              >
                {t('cpr_management.contact_modal.button.add')}
              </Button>
            ) : (
              <Button
                isLoading={isLoading}
                action="danger"
                emphasis="bold"
                onClick={() => mutate({ id: data.id })}
              >
                {deleteContact
                  ? t('common.button.delete')
                  : t('cpr_management_group_detail.button.unlink')}
              </Button>
            )}
          </div>
        </Stack>
      </Dialog>
      <AddCprContactModal
        contactGroupId={contactGroupId}
        open={isAddContactOpen}
        onOpenChange={setIsAddContactOpen}
      />
    </>
  );
};
